import { CORRECT_SEARCH_TERMS } from "../constants/searchTerms";

/**
 * Distanza di Levenshtein con riga singola (O(min(n,m)) di memoria invece di O(n*m)) e bail-out anticipato:
 * se durante il calcolo il valore minimo della riga corrente supera già maxDistance, la stringa non può
 * rientrare nella tolleranza e si interrompe subito il confronto (ritorna maxDistance + 1 come "scartata").
 */
const levenshteinDistance = (a: string, b: string, maxDistance: number): number => {
    if (Math.abs(a.length - b.length) > maxDistance) return maxDistance + 1;

    let previousRow = Array.from({ length: b.length + 1 }, (_, j) => j);

    for (let i = 1; i <= a.length; i++) {
        const currentRow = [i];
        let rowMin = currentRow[0];

        for (let j = 1; j <= b.length; j++) {
            currentRow[j] = a[i - 1] === b[j - 1]
                ? previousRow[j - 1]
                : Math.min(previousRow[j - 1] + 1, currentRow[j - 1] + 1, previousRow[j] + 1);
            rowMin = Math.min(rowMin, currentRow[j]);
        }

        if (rowMin > maxDistance) return maxDistance + 1; // nessuna possibilità di rientrare nella tolleranza

        previousRow = currentRow;
    }

    return previousRow[b.length];
};

/** Trova, tra i termini corretti, il più simile al termine cercato (null se non c'è già una corrispondenza esatta o se troppo diverso) */
export const findSuggestedCorrectTerm = (term: string): string | null => {
    const normalizedTerm = term?.trim().toLowerCase();
    const CorrectTermsKeys = Object.keys(CORRECT_SEARCH_TERMS)
    if (!normalizedTerm || CorrectTermsKeys.includes(normalizedTerm)) return null;

    let minDistance = Infinity;
    let sameDistances = []
    CorrectTermsKeys.forEach(correctTerm => {
        // confronto case-insensitive: senza toLowerCase ogni lettera maiuscola del termine corretto (es. "Norme&Tributi")
        // verrebbe contata come mismatch, gonfiando la distanza rispetto a parole più corte/tutte minuscole
        const distance = levenshteinDistance(normalizedTerm, correctTerm.toLowerCase(), minDistance);
        if (distance == minDistance) {
            sameDistances.push(correctTerm)
        } else if (distance < minDistance) {
            sameDistances = [correctTerm]
            minDistance = distance;
        }
    });

    if (sameDistances.length < 1) return null;
    //Delle parole con la stessa distanza di Levenshtein prendiamo quella a prioritàmaggiore.
    const closestTerm = sameDistances.reduce((chiaveMax, chiaveCorrente) => {
        // Confrontiamo il valore della chiave corrente con quello della chiave massima trovata finora
        if (CORRECT_SEARCH_TERMS[chiaveCorrente] > CORRECT_SEARCH_TERMS[chiaveMax]) {
            return chiaveCorrente; // Trovato un valore più alto! Aggiorniamo la chiave massima
        }
        return chiaveMax; // Altrimenti manteniamo la chiave massima precedente
    });

    // tolleranza: massimo la metà dei caratteri della parola corretta (minimo 1)
    const tolerance = Math.max(1, Math.floor(closestTerm.length / 2));
    return minDistance <= tolerance ? closestTerm : null;
};